import { createContext, useContext, useMemo, type ReactNode } from "react"
import { useDashboardDriftSummariesQuery } from "@/v2-backoffice-client/uptime/dashboard/dashboard.driftSummaries.query"
import { MAP_LAYER_MONITOR_SOURCE } from "@/v2-backoffice-client/layer/layer.constants"
import type { LayerDriftSummaryFragment } from "@/v2-backoffice-client/uptime/dashboard/__types__/dashboard.driftSummaries.fragment"

type Ctx = {
  byFeatureCode: Map<string, LayerDriftSummaryFragment>
  loading: boolean
}

const EMPTY_CTX: Ctx = {
  byFeatureCode: new Map(),
  loading: false,
}

const LayerDriftMapContext = createContext<Ctx>(EMPTY_CTX)

export type LayerDriftMapProviderProps = {
  children: ReactNode
}

// Wraps a subtree (typically a layer table) with one shared drift-status
// lookup, mirroring LayerUptimeMapProvider: the lean summaries query is
// run once and bucketed client-side by feature code, so a Drift cell per
// row costs no extra network round-trips.
export function LayerDriftMapProvider(
  props: Readonly<LayerDriftMapProviderProps>,
) {
  const { data, loading } = useDashboardDriftSummariesQuery({
    // Cells just want a snapshot; refetching on every mount would
    // hammer the API as the operator navigates the layer tables.
    fetchPolicy: "cache-first",
  })

  const value = useMemo<Ctx>(() => {
    const map = new Map<string, LayerDriftSummaryFragment>()
    for (const summary of data?.getLayerDriftSummaries ?? []) {
      if (summary.Source !== MAP_LAYER_MONITOR_SOURCE) continue
      map.set(summary.Identifier, summary)
    }
    return { byFeatureCode: map, loading }
  }, [data, loading])

  return (
    <LayerDriftMapContext.Provider value={value}>
      {props.children}
    </LayerDriftMapContext.Provider>
  )
}

export function useLayerDriftSummary(featureCode: string | null | undefined) {
  const ctx = useContext(LayerDriftMapContext)
  const summary = featureCode ? ctx.byFeatureCode.get(featureCode) : undefined
  return {
    summary: summary ?? null,
    // Loading only matters until the map is populated; once we have
    // entries any miss is a real "not fingerprinted" not a pending fetch.
    loading: ctx.loading && ctx.byFeatureCode.size === 0,
  }
}
